/**
 * «الدور الإداري» — one of the ready-made administration roles on an
 * administrative-manager account (المرحلة 1.1, P15), or none and the ticked
 * list as before.
 *
 * Each card says, in a line, what the role is for, what it can do (its
 * capabilities, by name) and the one thing it cannot: open a patient's file.
 * The bundle itself is the server's (lib/adminRoles.js); picking a card sends
 * only its name.
 */

import { Icon } from '../../components/common';
import { ADMIN_ROLES, PERMISSION_LABELS } from '../../domain/rbac';
import { useI18n } from '../../i18n/I18nProvider';

export const ADMIN_ROLE_LOOK = Object.freeze({
  operations: { icon: 'calendar-clock', tone: 'cyan' },
  finance: { icon: 'banknote', tone: 'emerald' },
  support: { icon: 'headset', tone: 'amber' },
  compliance: { icon: 'fingerprint', tone: 'rose' },
});

export function AdminRolePicker({ value, onChange, allowCustom = true }) {
  const { t, tr } = useI18n();
  const options = Object.keys(ADMIN_ROLES);

  return (
    <div className="admin-roles" data-testid="admin-role-picker">
      <div className="mb-3">
        <p className="text-sm font-bold text-white">{t('adminRoles.title')}</p>
        <p className="text-xs text-slate-400">{t('adminRoles.intro')}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((key) => {
          const look = ADMIN_ROLE_LOOK[key];
          const on = value === key;
          const holds = ADMIN_ROLES[key];
          return (
            <button key={key} type="button" className={`admin-role-card tone-${look.tone} ${on ? 'is-on' : ''}`} onClick={() => onChange(key)} aria-pressed={on} data-testid={`admin-role-${key}`}>
              <span className="flex items-start gap-3">
                <span className="tile3d__face shrink-0" style={{ width: 42, height: 42 }}>
                  <Icon name={look.icon} size={19} className="tile3d__icon" />
                </span>
                <span className="min-w-0 flex-1 text-start">
                  <span className="flex items-center gap-2 font-black text-white">
                    {t(`adminRoles.${key}`)}
                    {on && <Icon name="circle-check" size={16} className="text-emerald-400" />}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-400">{t(`adminRoles.${key}Hint`)}</span>
                </span>
              </span>
              <span className="admin-role-card__perms">
                {holds.slice(0, 4).map((name) => (
                  <span key={name} className="admin-role-card__perm">
                    <Icon name="check" size={11} />
                    <span className="truncate">{tr(PERMISSION_LABELS[name] || name)}</span>
                  </span>
                ))}
                {holds.length > 4 && <span className="admin-role-card__more">+{holds.length - 4}</span>}
                <span className="admin-role-card__perm is-no">
                  <Icon name="eye-off" size={11} />
                  <span className="truncate">
                    {t('adminRoles.cannot')} {t('adminRoles.openFiles')}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <button type="button" className={`admin-role-none ${!value ? 'is-on' : ''}`} onClick={() => onChange('')} aria-pressed={!value} data-testid="admin-role-none">
          <Icon name={!value ? 'circle-check' : 'circle-dashed'} size={15} />
          {t('adminRoles.none')}
        </button>
      )}
    </div>
  );
}

export default AdminRolePicker;
